import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import Select from 'tio-ui/components/select';
import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { task } from 'reactiveweb/ember-concurrency';
import type { ApplicationApprovalSummaryApprover } from '../../../types/tuition-assistance.ts';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type IntlService from 'ember-intl/services/intl';
import type { Store as StoreService } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import { insertReviewerRequest } from 'tio-data/requests/tas-program-instance';
import { reloadRecord } from 'tio-data/requests/save-record';
import { insertReviewerRequest as insertTasApplicationReviewerRequest } from 'tio-data/requests/tas-application-actions';
import type { TasProgramInstanceExtension as TasProgramInstance } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TaskInstance } from 'reactiveweb/ember-concurrency';
import type { NotificationsService } from 'tio-ui/components/notifications';

const INPUT_DEBOUNCE = /* config.environment === 'test' ? 0 : */ 250;

interface ReassignModalSignature {
  show: boolean;
  onClose: () => void;
  approverToReassign: ApplicationApprovalSummaryApprover;
  application: TasApplicationModel | TasProgramInstance;
  companyId: string;
}

export default class ReassignModalComponent extends Component<ReassignModalSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;

  @tracked selectedApproverId = '';
  @tracked searchTerm = '';

  onSearch = (value: string) => {
    this.searchTerm = value;
  };

  handleSelectionChange = async (key: string | null) => {
    this.selectedApproverId = key || '';
  };

  insertReviewer = dropTask(async () => {
    if (!this.selectedApproverId) return;
    const ordinal = this.args.approverToReassign.ordinal;
    const employeeId = Number(this.selectedApproverId);

    if (this.args.application.modelName === 'tas-application') {
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // store.adapterFor('tas-application').insertReviewer(application, employeeId, ordinal)
      // (PATCH /tas-applications/:id/insert-reviewer + reload). Distinct endpoint from the
      // tas-program-instance builder used by the other branch below.
      await this.store.request(
        insertTasApplicationReviewerRequest(this.args.application.id as string, employeeId, ordinal)
      );
      return reloadRecord(this.store, this.args.application);
    }

    await this.store.request(
      insertReviewerRequest(this.args.application.id as string, employeeId, ordinal)
    );
    // Legacy adapter#insertReviewer reloaded the program-instance before
    // returning so the parent saw the new approver chain. The builder
    // shape is pure, so re-do the reload here.
    return reloadRecord(this.store, this.args.application);
  });

  searchApprovers = restartableTask(async (search: string) => {
    if (!search) {
      return [];
    }
    await timeout(INPUT_DEBOUNCE);
    const { content } = (await this.store.request(
      query<EmployeeModel>('employee', {
        'filter[company_id]': this.args.companyId,
        'filter[search]': search,
      })
    )) as { content: { data: EmployeeModel[] } };
    return content.data.map((employee) => ({
      key: employee.id!,
      label: employee.fullName || employee.id!,
    }));
  });

  fetchApproversTask: TaskInstance<Array<{ key: string; label: string }>> = task(
    this,
    this.searchApprovers,
    () => [this.searchTerm]
  );

  get items() {
    return this.fetchApproversTask.value ?? [];
  }

  onClose = () => {
    this.selectedApproverId = '';
    this.searchTerm = '';
    this.args.onClose();
  };

  submit = async () => {
    if (!this.selectedApproverId) return;

    try {
      await this.insertReviewer.perform();
      this.notifications.add('Successfully reassigned approver', {
        appearance: 'success',
      });
      this.onClose();
    } catch (e) {
      console.error(e);
      this.notifications.add('Something went wrong', {
        appearance: 'error',
      });
    }
  };

  <template>
    <Modal @isOpen={{@show}} @onClose={{this.onClose}} class="min-h-64">
      <:header>{{t "tuition_assistance.approval_queue.reassign"}}</:header>
      <:body>
        <div class="tio-col-flush gap-y-2">
          <Select
            data-test-approver-select
            @label={{t "tuition_assistance.approval_queue.select_replacement_approver"}}
            @searchable={{true}}
            @onSearch={{this.onSearch}}
            @isLoading={{this.fetchApproversTask.isRunning}}
            @selected={{this.selectedApproverId}}
            @items={{this.items}}
            @onChange={{this.handleSelectionChange}}
            @clearable={{true}}
          />
        </div>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.insertReviewer.isRunning}}
          @onCancel={{this.onClose}}
          @onSubmit={{this.submit}}
        />
      </:footer>
    </Modal>
  </template>
}
